import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { FileText, Phone, Mail, Calendar, LogOut, CheckCheck, MessageSquare, Plus, Clock, AlertCircle, Archive, Eye, EyeOff, Search, X, Bot, Trash2, Camera, Calculator } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { StudioPhotos } from './StudioPhotos';
import { PricingTool } from './PricingTool';
import { UniversalMessageModal } from './UniversalMessageModal';
import { MessageHistory } from './MessageHistory';
import { NewMessageModal } from './NewMessageModal';
import { DetailModal } from './DetailModal';
import BotManagementComplete from './BotManagementComplete';

// Toujours utiliser window.location.origin pour les appels API (évite les problèmes de cache de build)
const API_URL = window.location.origin;


export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [smsMessages, setSmsMessages] = useState([]);  // Messages SMS entrants
  const [botConversations, setBotConversations] = useState([]);  // Conversations Bot IA

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [activeTab, setActiveTab] = useState('rdv');
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [showUniversalMessageModal, setShowUniversalMessageModal] = useState(false);
  const [selectedItemForMessage, setSelectedItemForMessage] = useState(null);
  const [messageItemType, setMessageItemType] = useState(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, markAllAsSeen } = useNotifications();
  
  const rdvAlertAudioRef = useRef(null);
  const [playingRdvAlert, setPlayingRdvAlert] = useState(false);
  const [lastNotificationCount, setLastNotificationCount] = useState(0);
  const [showWhatsAppIndicator, setShowWhatsAppIndicator] = useState(true);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [selectedRdvIds, setSelectedRdvIds] = useState(new Set());
  // SMS reply state (doit être au niveau composant, pas dans renderSMS)
  const [smsReplyText, setSmsReplyText] = useState('');
  const [smsReplyingTo, setSmsReplyingTo] = useState(null);
  const [smsSendingReply, setSmsSendingReply] = useState(false);
  const audioContextRef = useRef(null);
  const soundIntervalRef = useRef(null);
  // Alerte vocale agent — déclenchée même hors onglet Bot
  const agentAlertRef = useRef(false);
  const agentAlertIntervalRef = useRef(null);

  useEffect(() => {
    // Chargement initial
    fetchQuotes();
    fetchDiagnostics();
    fetchAppointments();
    fetchSmsMessages();
    fetchBotConversations();
    
    // Polling simple toutes les 5 secondes
    const interval = setInterval(() => {
      fetchQuotes();
      fetchDiagnostics();
      fetchAppointments();
      fetchSmsMessages();
      fetchBotConversations();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Système d'alerte - son en BOUCLE jusqu'à consultation
  useEffect(() => {
    const newRdv = appointments.filter(a => a.isNew).length;
    const newDevis = quotes.filter(q => q.isNew).length;
    const newDiag = diagnostics.filter(d => d.isNew).length;
    const newWA = smsMessages.filter(m => m.isNew).length;
    const newBot = botConversations.filter(c => c.isNew && !c.archived).length;
    const total = newRdv + newDevis + newDiag + newWA + newBot;
    
    // Si nouvelle demande détectée
    if (total > lastNotificationCount) {
      console.log('🔔 Nouvelle demande !', {newRdv, newDevis, newDiag, newWA, newBot, total});
      setLastNotificationCount(total);
      setHasNewNotification(true);
      
      // Créer contexte audio si besoin
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // Jouer son immédiatement
      playNotificationSound();
      
      // Démarrer la boucle si pas déjà active
      if (!soundIntervalRef.current) {
        soundIntervalRef.current = setInterval(() => {
          playNotificationSound();
        }, 10000); // Toutes les 10 secondes
        console.log('🔊 Boucle sonore démarrée');
      }
    } 
    // Si plus de nouvelles demandes, arrêter le son
    else if (total === 0 && soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
      setHasNewNotification(false);
      setLastNotificationCount(0);
      console.log('🔇 Boucle sonore arrêtée (plus de demandes)');
    }
    // Mise à jour du compteur
    else if (total < lastNotificationCount) {
      setLastNotificationCount(total);
      // Si plus rien, arrêter le son
      if (total === 0 && soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
        setHasNewNotification(false);
        console.log('🔇 Boucle sonore arrêtée');
      }
    }
  }, [appointments, quotes, diagnostics, smsMessages, botConversations, lastNotificationCount]);

  // Alerte sonore pour nouveaux SMS (hors onglet SMS)
  useEffect(() => {
    const newSms = smsMessages.filter(m => m.isNew);
    if (newSms.length > 0 && activeTab !== 'whatsapp') {
      playNotificationSound();
    }
  }, [smsMessages]);
  // Quand l'admin est sur RDV, Devis, Diagnostic ou SMS,
  // BotManagementComplete n'est pas monté — on surveille ici.
  useEffect(() => {
    const urgentConvs = botConversations.filter(
      c => (c.agent_requested === true || c.urgent === true) && !c.agent_requested_seen && !c.archived
    );

    if (urgentConvs.length > 0 && activeTab !== 'bot') {
      // Première détection → voix immédiate + beep
      if (!agentAlertRef.current) {
        agentAlertRef.current = true;
        speakAgentAlertGlobal();
        playNotificationSound();
        console.log('🚨 [GLOBAL] Agent demandé — alerte vocale déclenchée');
      }
      // Démarrer l'intervalle répétitif (toutes les 20s)
      if (!agentAlertIntervalRef.current) {
        agentAlertIntervalRef.current = setInterval(() => {
          speakAgentAlertGlobal();
          playNotificationSound();
        }, 20000);
      }
    } else {
      // Plus d'urgences (ou on est sur l'onglet Bot) → stopper
      if (agentAlertRef.current) {
        agentAlertRef.current = false;
        if (agentAlertIntervalRef.current) {
          clearInterval(agentAlertIntervalRef.current);
          agentAlertIntervalRef.current = null;
        }
        console.log('🔇 [GLOBAL] Alerte agent stoppée');
      }
    }
  }, [botConversations, activeTab]);
  
  // SON DÉSACTIVÉ - Pas de bip pour les RDV
  const playRdvAlert = () => {
    // Son désactivé par l'utilisateur
    console.log('🔇 Son RDV désactivé');
  };

  // Alerte vocale "Un client demande un agent" — GLOBALE (tous onglets sauf Bot)
  const speakAgentAlertGlobal = () => {
    try {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance('Un client demande un agent. Veuillez intervenir.');
        utterance.lang = 'fr-FR';
        utterance.rate = 0.85;
        utterance.volume = 1;
        utterance.pitch = 1;
        window.speechSynthesis.speak(utterance);
      }
    } catch (e) {
      console.error('Erreur alerte vocale globale:', e);
    }
  };
  
  const stopRdvAlert = () => {
    if (rdvAlertAudioRef.current) {
      rdvAlertAudioRef.current.pause();
      rdvAlertAudioRef.current.currentTime = 0;
      setPlayingRdvAlert(false);
    }
  };
  
  const handleOpenMessageModal = (item, type) => {
    setSelectedItemForMessage(item);
    setMessageItemType(type || (item.date ? 'rdv' : item.deviceType ? (item.address ? 'diagnostic' : 'devis') : 'devis'));
    setShowUniversalMessageModal(true);
  };

  const handleCloseUniversalModal = () => {
    setShowUniversalMessageModal(false);
    setSelectedItemForMessage(null);
    setMessageItemType(null);
  };

  const handleMessageSent = () => {
    fetchQuotes();
    fetchAppointments();
    fetchDiagnostics();
  };
  
  // Fonction pour arrêter le son en boucle et acquitter les notifications GLOBALEMENT
  const stopNotificationLoop = async () => {
    // Arrêter l'intervalle sonore
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
      console.log('🔇 Boucle sonore arrêtée');
    }
    
    // Réinitialiser les états visuels
    setHasNewNotification(false);
    
    // CRUCIAL : Appeler le backend pour marquer TOUT comme lu (multi-appareils)
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/acknowledge-all-notifications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        console.log('✅ Toutes les notifications acquittées sur le serveur');
        
        // Recharger les données (isNew=false dans la DB) EN ATTENDANT la fin
        await Promise.all([
          fetchAppointments(),
          fetchQuotes(),
          fetchDiagnostics(),
          fetchSmsMessages(),
          fetchBotConversations()
        ]);
        
        // Réinitialiser le compteur SEULEMENT après le rechargement
        setLastNotificationCount(0);
        console.log('✅ Compteur réinitialisé après rechargement des données');
      }
    } catch (error) {
      console.error('Erreur acquittement notifications:', error);
      setLastNotificationCount(0); // Sécurité
    }
  };

  // Récupérer les messages SMS
  const fetchSmsMessages = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/sms-messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSmsMessages(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des SMS:', error);
    }
  };

  // Récupérer les conversations Bot IA
  const fetchBotConversations = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/chat/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBotConversations(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des conversations Bot:', error);
    }
  };

  // Fonction pour jouer un son d'alerte
  const playNotificationSound = async () => {
    try {
      console.log('🎵 Création du contexte audio...');
      
      // Créer ou réutiliser le contexte audio
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      
      // Reprendre le contexte s'il est suspendu
      if (audioContext.state === 'suspended') {
        console.log('⏸️ Contexte audio suspendu, tentative de reprise...');
        await audioContext.resume();
      }
      
      console.log('🔊 État du contexte audio:', audioContext.state);
      
      // Son 1 - Note basse
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();
      
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);
      
      oscillator1.type = 'sine';
      oscillator1.frequency.value = 800;
      gainNode1.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode1.gain.linearRampToValueAtTime(0.4, audioContext.currentTime + 0.01);
      gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator1.start(audioContext.currentTime);
      oscillator1.stop(audioContext.currentTime + 0.2);
      
      // Son 2 - Note haute (après un délai)
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        
        oscillator2.type = 'sine';
        oscillator2.frequency.value = 1000;
        gainNode2.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode2.gain.linearRampToValueAtTime(0.4, audioContext.currentTime + 0.01);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
        
        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.25);
        
        console.log('✅ Son de notification joué avec succès !');
      }, 150);
      
    } catch (error) {
      console.error('❌ Erreur lecture son:', error);
      alert('⚠️ Pour activer les alertes sonores, cliquez d\'abord sur le bouton 🔊 de test');
    }
  };

  // Marquer comme lu quand on clique sur un onglet
  const handleTabClick = async (tabKey) => {
    setActiveTab(tabKey);
    setShowArchived(false);
    setSearchQuery('');
    
    // Arrêter le son en boucle
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
      setHasNewNotification(false);
      console.log('🔇 Son arrêté (consultation)');
    }
    
    // Marquer les items de cet onglet comme lus
    try {
      const token = localStorage.getItem('admin_token');
      let endpoint = '';
      
      if (tabKey === 'rdv') endpoint = '/api/appointments/mark-all-read';
      else if (tabKey === 'devis') endpoint = '/api/quotes/mark-all-read';
      else if (tabKey === 'diagnostic') endpoint = '/api/diagnostics/mark-all-read';
      else if (tabKey === 'whatsapp') endpoint = '';  // SMS tab removed
      
      if (endpoint) {
        await fetch(`${API_URL}${endpoint}`, {
          method: 'POST',
          headers: {'Authorization': `Bearer ${token}`}
        });
        
        // Rafraîchir les données
        setTimeout(() => {
          if (tabKey === 'rdv') fetchAppointments();
          else if (tabKey === 'devis') fetchQuotes();
          else if (tabKey === 'diagnostic') fetchDiagnostics();
          else if (tabKey === 'whatsapp') fetchSmsMessages();
        }, 500);
      }
    } catch (error) {
      console.error('Erreur marquage lecture:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_URL}/api/appointments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments || []);
      }
    } catch (error) {
      console.error('Erreur chargement RDV:', error);
    }
  };
  
  const fetchDiagnostics = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_URL}/api/diagnostics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDiagnostics(data || []);
      }
    } catch (error) {
      console.error('Erreur chargement diagnostics:', error);
    }
  };

  // Note: fetchSmsMessages est déjà défini plus haut (ligne ~199)


  const fetchQuotes = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_URL}/api/quotes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 401) {
        logout();
        navigate('/');
        return;
      }
      
      const data = await response.json();
      setQuotes(data);
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmAppointment = async (appointmentId) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_URL}/api/appointments/${appointmentId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'confirmed-by-admin' })
      });
      
      if (response.ok) {
        fetchAppointments();
        stopRdvAlert();
      }
    } catch (error) {
      console.error('Erreur confirmation RDV:', error);
    }
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce rendez-vous ? Un email sera envoyé au client.')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_URL}/api/appointments/${appointmentId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('RDV annulé. Email envoyé au client.');
        fetchAppointments();
      } else {
        alert('Erreur lors de l\'annulation');
      }
    } catch (error) {
      console.error('Erreur annulation RDV:', error);
      alert('Erreur lors de l\'annulation');
    }
  };

  const openModifyModal = (appointment) => {
    setSelectedAppointment(appointment);
    setShowModifyModal(true);
  };

  const closeModifyModal = () => {
    setShowModifyModal(false);
    setSelectedAppointment(null);
  };

  const modifyAppointment = async (newDate, newTime) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_URL}/api/appointments/${selectedAppointment.id}/modify`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          new_date: newDate,
          new_time: newTime
        })
      });
      
      if (response.ok) {
        alert('RDV modifié. Email envoyé au client.');
        closeModifyModal();
        fetchAppointments();
      } else {
        alert('Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur modification RDV:', error);
      alert('Erreur lors de la modification');
    }
  };

  const openDetailModal = (item, type) => {
    setSelectedDetail(item);
    setDetailType(type);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedDetail(null);


    setDetailType(null);
  };

  const deleteItem = async (id, type) => {
    const labels = { quote: 'ce devis', diagnostic: 'ce diagnostic', appointment: 'ce rendez-vous' };
    if (!window.confirm(`⚠️ Supprimer définitivement ${labels[type] || 'cet élément'} ? Cette action est irréversible.`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      const endpoints = {
        quote: `/api/quotes/${id}`,
        diagnostic: `/api/diagnostics/${id}`,
        appointment: `/api/appointments/${id}`
      };
      const response = await fetch(`${API_URL}${endpoints[type]}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        if (type === 'quote') fetchQuotes();
        else if (type === 'diagnostic') fetchDiagnostics();
        else if (type === 'appointment') fetchAppointments();
      } else {
        alert('❌ Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('❌ Erreur réseau');
    }
  };

  const deleteAllItems = async (type) => {
    const labels = {
      rdv: 'TOUS les rendez-vous',
      devis: 'TOUS les devis',
      diagnostic: 'TOUS les diagnostics'
    };
    const label = labels[type] || 'tous ces éléments';
    if (!window.confirm(`⚠️ ATTENTION — Supprimer définitivement ${label} ? Cette action est IRRÉVERSIBLE.`)) return;
    if (!window.confirm(`Dernière confirmation : supprimer définitivement ${label} ?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      const endpoints = {
        rdv: `/api/appointments/bulk/all`,
        devis: `/api/quotes/bulk/all`,
        diagnostic: `/api/diagnostics/bulk/all`
      };
      const response = await fetch(`${API_URL}${endpoints[type]}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}`);
        if (type === 'rdv') fetchAppointments();
        else if (type === 'devis') fetchQuotes();
        else if (type === 'diagnostic') fetchDiagnostics();
      } else {
        alert('❌ Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression totale:', error);
      alert('❌ Erreur réseau');
    }
  };

  const deleteSelectedRdv = async () => {
    const count = selectedRdvIds.size;
    if (count === 0) return;
    if (!window.confirm(`⚠️ Supprimer définitivement ${count} rendez-vous sélectionné(s) ? Action irréversible.`)) return;
    const token = localStorage.getItem('admin_token');
    let deletedCount = 0;
    for (const id of selectedRdvIds) {
      try {
        const resp = await fetch(`${API_URL}/api/appointments/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) deletedCount++;
      } catch (e) {
        console.error(`Erreur suppression RDV ${id}:`, e);
      }
    }
    setSelectedRdvIds(new Set());
    fetchAppointments();
    alert(`✅ ${deletedCount} / ${count} rendez-vous supprimé(s)`);
  };

  const toggleRdvSelection = (id) => {
    setSelectedRdvIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const archiveItem = async (id, type) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      let endpoint = '';
      let method = 'POST';
      let body = {};
      
      if (type === 'quote') {
        endpoint = `/api/quotes/${id}/archive`;
      } else if (type === 'diagnostic') {
        endpoint = `/api/diagnostics/${id}/archive`;
      } else if (type === 'appointment') {
        endpoint = `/api/appointments/${id}/status`;
        method = 'PATCH';
        body = { status: 'archived' };
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        fetchQuotes();
        fetchDiagnostics();
        fetchAppointments();
      }
    } catch (error) {
      console.error('Erreur archivage:', error);
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleMarkAllSeen = () => {
    // Ne pas arrêter le son ici - seulement avec le badge WhatsApp
    markAllAsSeen();
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setSelectedQuote(null);
  };

  const toggleMessageHistory = (quoteId) => {
    setExpandedMessages(prev => ({
      ...prev,
      [quoteId]: !prev[quoteId]
    }));
  };

  const handleOpenNewMessageModal = () => {
    setShowNewMessageModal(true);
  };

  const handleCloseNewMessageModal = () => {
    setShowNewMessageModal(false);
  };

  const handleNewMessageSent = () => {
    // Optionnel: rafraîchir si nécessaire
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrer et rechercher
  const filterAndSearch = (items, archived) => {
    return items
      .filter(item => {
        const isArchived = item.archived || item.status === 'archived' || item.status === 'completed';
        return archived ? isArchived : !isArchived;
      })
      .filter(item => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          item.name?.toLowerCase().includes(query) ||
          item.email?.toLowerCase().includes(query) ||
          item.phone?.includes(query) ||
          item.brand?.toLowerCase().includes(query) ||
          item.model?.toLowerCase().includes(query) ||
          item.device?.toLowerCase().includes(query)
        );
      });
  };

  const recentAppointments = filterAndSearch(appointments, false);
  const archivedAppointments = filterAndSearch(appointments, true);
  const recentQuotes = filterAndSearch(quotes, false);
  const archivedQuotes = filterAndSearch(quotes, true);
  const recentDiagnostics = filterAndSearch(diagnostics, false);
  const archivedDiagnostics = filterAndSearch(diagnostics, true);

  // Compter le total de nouvelles demandes
  const totalNewRequests = recentAppointments.length + recentQuotes.length + recentDiagnostics.length;
  const hasNewRequests = totalNewRequests > 0;

  // Rendus conditionnels pour chaque onglet
  const renderAppointments = () => {
    const dataToShow = showArchived ? archivedAppointments : recentAppointments;
    const priorityRdv = appointments.filter(apt => apt.status === 'confirmed');

    return (
      <div className="space-y-6">
        {/* Section RDV PRIORITAIRES */}
        {priorityRdv.length > 0 && !showArchived && (
          <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 rounded-2xl shadow-2xl animate-pulse">
            <h2 className="text-2xl font-bold text-white flex items-center space-x-3 mb-4">
              <AlertCircle className="w-8 h-8 animate-bounce" />
              <span>🔥 RENDEZ-VOUS PRIORITAIRES - ACTION REQUISE</span>
            </h2>
            <p className="text-orange-100 mb-6">
              {priorityRdv.length} RDV en attente de confirmation
            </p>
            
            <div className="grid gap-4">
              {priorityRdv.map((appointment) => {
                const appointmentDate = new Date(appointment.date + 'T' + appointment.time);
                const now = new Date();
                const hoursUntil = Math.floor((appointmentDate - now) / (1000 * 60 * 60));
                const minutesUntil = Math.floor(((appointmentDate - now) % (1000 * 60 * 60)) / (1000 * 60));
                
                return (
                  <Card key={appointment.id} className="border-4 border-orange-500 shadow-2xl bg-white">
                    <CardHeader className="bg-gradient-to-r from-orange-100 to-red-100">
                      <div className="flex justify-between items-start flex-wrap gap-4">
                        <div>
                          <CardTitle className="text-2xl flex items-center space-x-3 flex-wrap gap-2">
                            <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold animate-pulse">
                              🔥 PRIORITAIRE
                            </span>
                            <span>{appointment.name}</span>
                          </CardTitle>
                          <CardDescription className="text-lg mt-2">
                            📅 {new Date(appointment.date).toLocaleDateString('fr-FR', { 
                              weekday: 'long', day: 'numeric', month: 'long' 
                            })} à {appointment.time}
                          </CardDescription>
                        </div>
                        
                        <div className="text-right">
                          <div className="bg-orange-500 text-white px-4 py-2 rounded-lg font-bold text-lg">
                            {hoursUntil > 0 ? `${hoursUntil}h ${minutesUntil}min` : `${minutesUntil}min`}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Temps restant</p>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-6">
                      <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <div className="flex items-center space-x-3">
                          <Phone className="w-5 h-5 text-blue-600" />
                          <a href={`tel:${appointment.phone}`} className="text-blue-600 hover:underline font-semibold">
                            {appointment.phone}
                          </a>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-blue-600" />
                          <a href={`mailto:${appointment.email}`} className="text-blue-600 hover:underline">
                            {appointment.email}
                          </a>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <FileText className="w-5 h-5 text-gray-600" />
                          <span className="font-semibold">{appointment.device}</span>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <Clock className="w-5 h-5 text-gray-600" />
                          <span>{appointment.service} ({appointment.duration}h)</span>
                        </div>
                      </div>
                      
                      {appointment.message && (
                        <div className="bg-blue-50 p-4 rounded-lg mb-4">
                          <p className="text-sm text-gray-700"><strong>Message :</strong> {appointment.message}</p>
                        </div>
                      )}
                      
                      <Button
                        onClick={() => confirmAppointment(appointment.id)}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 text-lg shadow-lg"
                      >
                        ✅ CONFIRMER CE RENDEZ-VOUS (Arrête l'alerte)
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Liste des RDV */}
        {dataToShow.length === 0 ? (
          <Card className="border-blue-200">
            <CardContent className="py-12 text-center">
              <Calendar className="mx-auto mb-4 text-blue-400" size={48} />
              <p className="text-gray-600">
                {showArchived ? 'Aucun RDV archivé' : 'Aucun RDV récent'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {dataToShow.map((appointment) => (
              <Card key={appointment.id} className={`hover:shadow-lg transition-shadow border-blue-200 ${selectedRdvIds.has(appointment.id) ? 'ring-2 ring-orange-500 bg-orange-50' : ''}`}>
                <CardHeader className="bg-blue-50">
                  <div className="flex justify-between items-start flex-wrap gap-4">
                    {/* Checkbox de sélection */}
                    <input
                      type="checkbox"
                      checked={selectedRdvIds.has(appointment.id)}
                      onChange={() => toggleRdvSelection(appointment.id)}
                      className="mt-1 h-5 w-5 accent-orange-500 cursor-pointer flex-shrink-0"
                      title="Sélectionner pour suppression"
                      onClick={e => e.stopPropagation()}
                    />
                    <div 
                      className="flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => openDetailModal(appointment, 'rdv')}
                    >
                      <CardTitle className="text-xl text-blue-900 flex items-center">
                        {appointment.name}
                        <Eye size={18} className="ml-2 text-blue-600" />
                      </CardTitle>
                      <CardDescription className="flex items-center mt-1">
                        <Calendar size={14} className="mr-1" />
                        📅 {new Date(appointment.date).toLocaleDateString('fr-FR')} à {appointment.time}
                      </CardDescription>
                    </div>
                    <Badge className="bg-blue-500">{appointment.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <Phone size={16} className="text-blue-600" />
                      <a href={`tel:${appointment.phone}`} className="text-blue-600 hover:underline">
                        {appointment.phone}
                      </a>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail size={16} className="text-blue-600" />
                      <a href={`mailto:${appointment.email}`} className="text-blue-600 hover:underline">
                        {appointment.email}
                      </a>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div><strong>Appareil:</strong> {appointment.device}</div>
                    <div><strong>Service:</strong> {appointment.service}</div>
                    <div><strong>Durée:</strong> {appointment.duration}h</div>
                    {appointment.message && (
                      <div className="pt-2 border-t">
                        <strong>Message:</strong> {appointment.message}
                      </div>
                    )}
                  </div>
                  
                  {!showArchived && (
                    <div className="mt-4 flex flex-wrap gap-3">
                      <Button
                        onClick={() => openDetailModal(appointment, 'rdv')}
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        <Eye size={16} className="mr-2" />
                        Consulter
                      </Button>
                      <button
                        onClick={() => handleOpenMessageModal(appointment, 'rdv')}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors space-x-2"
                      >
                        <MessageSquare size={16} />
                        <span>Répondre par email</span>
                      </button>
                      <a
                        href={`tel:${appointment.phone}`}
                        className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors space-x-2"
                      >
                        <Phone size={16} />
                        <span>Appeler</span>
                      </a>
                      <Button
                        onClick={() => archiveItem(appointment.id, 'appointment')}
                        className="bg-gray-600 hover:bg-gray-700 text-white"
                      >
                        <Archive size={16} className="mr-2" />
                        Archiver
                      </Button>
                      <Button
                        onClick={() => openModifyModal(appointment)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        ✏️ Modifier
                      </Button>
                      <Button
                        onClick={() => cancelAppointment(appointment.id)}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        ❌ Annuler
                      </Button>
                      <Button
                        onClick={async () => {
                          const token = localStorage.getItem('admin_token');
                          try {
                            const resp = await fetch(`${API_URL}/api/appointments/${appointment.id}/send-reminder`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const data = await resp.json();
                            if (resp.ok) {
                              alert(`✅ Rappel SMS envoyé à ${appointment.phone}`);
                            } else {
                              alert(`❌ ${data.detail || 'Erreur envoi SMS'}`);
                            }
                          } catch (e) {
                            alert('❌ Erreur réseau');
                          }
                        }}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white"
                        title="Envoyer un rappel SMS au client"
                      >
                        📱 Rappel SMS
                      </Button>
                      <Button
                        onClick={() => deleteItem(appointment.id, 'appointment')}
                        className="bg-red-900 hover:bg-red-950 text-white"
                        title="Supprimer définitivement"
                      >
                        🗑️ Supprimer
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderQuotes = () => {
    const dataToShow = showArchived ? archivedQuotes : recentQuotes;

    return (
      <div className="space-y-4">
        {/* Carte de notification WhatsApp - Visible uniquement si nouvelle demande ET pas en mode archives */}
        {hasNewNotification && !showArchived && (
          <Card className="border-green-500 border-4 bg-gradient-to-r from-green-50 to-emerald-50 shadow-2xl animate-pulse">
            <CardContent className="py-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-green-500 p-3 rounded-full animate-bounce">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-900 flex items-center space-x-2">
                      <AlertCircle className="w-6 h-6 animate-spin" />
                      <span>🔔 Nouvelle demande de devis via WhatsApp !</span>
                    </h3>
                    <p className="text-green-700 mt-1">Un client a envoyé une demande. Consultez votre WhatsApp pour répondre.</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    stopNotificationLoop();
                    window.open('https://wa.me/33758250882', '_blank');
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg transition-all hover:scale-105 animate-pulse flex items-center space-x-2"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span>Consulter WhatsApp</span>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {dataToShow.length === 0 ? (
          <Card className="border-green-200">
            <CardContent className="py-12 text-center">
              <FileText className="mx-auto mb-4 text-green-400" size={48} />
              <p className="text-gray-600">
                {showArchived ? 'Aucun devis archivé' : 'Aucune demande de devis récente'}
              </p>
            </CardContent>
          </Card>
        ) : (
          dataToShow.map((quote) => (
            <Card key={quote.id} className="hover:shadow-lg transition-shadow border-green-200">
              <CardHeader className="bg-green-50">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div 
                    className="flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openDetailModal(quote, 'devis')}
                  >
                    <CardTitle className="text-xl text-green-900 flex items-center">
                      {quote.name}
                      <Eye size={18} className="ml-2 text-green-600" />
                    </CardTitle>
                    <CardDescription className="flex items-center mt-1">
                      <Calendar size={14} className="mr-1" />
                      {formatDate(quote.timestamp)}
                    </CardDescription>
                  </div>
                  <Badge className="bg-green-500">{quote.status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Phone size={16} className="text-green-600" />
                    <a href={`tel:${quote.phone}`} className="text-green-600 hover:underline">
                      {quote.phone}
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail size={16} className="text-green-600" />
                    <a href={`mailto:${quote.email}`} className="text-green-600 hover:underline">
                      {quote.email}
                    </a>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid md:grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="font-semibold">Type:</span>{' '}
                      <span className="capitalize">{quote.deviceType}</span>
                    </div>
                    {quote.brand && (
                      <div>
                        <span className="font-semibold">Marque:</span>{' '}
                        <span className="capitalize">{quote.brand}</span>
                      </div>
                    )}
                    {quote.model && (
                      <div>
                        <span className="font-semibold">Modèle:</span> {quote.model}
                      </div>
                    )}
                  </div>

                  {quote.promo_code && (
                    <div className="pt-2 border-t bg-purple-50 -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm text-purple-900 mb-1">🎁 Code Promo Client</p>
                          <p className="text-2xl font-bold text-purple-700 tracking-wider">{quote.promo_code}</p>
                          <p className="text-xs text-purple-600 mt-1">À présenter en boutique</p>
                        </div>
                        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                          OFFRE ACTIVE
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-2 border-t">
                    <p className="font-semibold text-sm mb-1">Problème:</p>
                    <p className="text-gray-700">{quote.problem}</p>
                  </div>

                  {quote.message && (
                    <div className="pt-2 border-t">
                      <p className="font-semibold text-sm mb-1">Message:</p>
                      <p className="text-gray-700">{quote.message}</p>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={() => openDetailModal(quote, 'devis')}
                    className="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors space-x-2"
                  >
                    <Eye size={16} />
                    <span>Consulter</span>
                  </button>
                  <button
                    onClick={() => handleOpenMessageModal(quote, 'devis')}
                    className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors space-x-2"
                  >
                    <MessageSquare size={16} />
                    <span>Envoyer un message</span>
                  </button>
                  <a
                    href={`tel:${quote.phone}`}
                    className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors space-x-2"
                  >
                    <Phone size={16} />
                    <span>Appeler</span>
                  </a>
                  {!showArchived && (
                    <button
                      onClick={() => archiveItem(quote.id, 'quote')}
                      className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors space-x-2"
                    >
                      <Archive size={16} />
                      <span>Archiver</span>
                    </button>
                  )}
                  <button
                    onClick={() => deleteItem(quote.id, 'quote')}
                    className="inline-flex items-center px-4 py-2 bg-red-800 hover:bg-red-900 text-white rounded-lg font-medium transition-colors space-x-2"
                    title="Supprimer définitivement"
                  >
                    <Trash2 size={16} />
                    <span>Supprimer</span>
                  </button>
                </div>
                
                <div className="mt-4">
                  <button
                    onClick={() => toggleMessageHistory(quote.id)}
                    className="text-sm text-green-600 hover:text-green-800 font-medium flex items-center space-x-1"
                  >
                    <MessageSquare size={14} />
                    <span>{expandedMessages[quote.id] ? 'Masquer' : 'Voir'} l'historique des messages</span>
                  </button>
                  
                  {expandedMessages[quote.id] && (
                    <div className="mt-3 pt-3 border-t">
                      <MessageHistory quoteId={quote.id} />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    );
  };

  const renderDiagnostics = () => {
    const dataToShow = showArchived ? archivedDiagnostics : recentDiagnostics;

    return (
      <div className="space-y-4">
        {dataToShow.length === 0 ? (
          <Card className="border-orange-200">
            <CardContent className="py-12 text-center">
              <AlertCircle className="mx-auto mb-4 text-orange-400" size={48} />
              <p className="text-gray-600">
                {showArchived ? 'Aucun diagnostic archivé' : 'Aucune demande de diagnostic récente'}
              </p>
            </CardContent>
          </Card>
        ) : (
          dataToShow.map((diagnostic) => (
            <Card key={diagnostic.id} className="hover:shadow-lg transition-shadow border-orange-200">
              <CardHeader className="bg-orange-50">
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div 
                    className="flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openDetailModal(diagnostic, 'diagnostic')}
                  >
                    <CardTitle className="text-xl text-orange-900 flex items-center">
                      {diagnostic.name}
                      <Eye size={18} className="ml-2 text-orange-600" />
                    </CardTitle>
                    <CardDescription className="flex items-center mt-1">
                      <Calendar size={14} className="mr-1" />
                      {formatDate(diagnostic.timestamp)}
                    </CardDescription>
                  </div>
                  {diagnostic.isNew && !showArchived && (
                    <Badge className="bg-orange-500 animate-pulse">Nouveau</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Phone size={16} className="text-orange-600" />
                    <a href={`tel:${diagnostic.phone}`} className="text-orange-600 hover:underline">
                      {diagnostic.phone}
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail size={16} className="text-orange-600" />
                    <a href={`mailto:${diagnostic.email}`} className="text-orange-600 hover:underline">
                      {diagnostic.email}
                    </a>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid md:grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="font-semibold">Type:</span>{' '}
                      <span className="capitalize">{diagnostic.deviceType}</span>
                    </div>
                    {diagnostic.brand && (
                      <div>
                        <span className="font-semibold">Marque:</span>{' '}
                        <span className="capitalize">{diagnostic.brand}</span>
                      </div>
                    )}
                    {diagnostic.model && (
                      <div>
                        <span className="font-semibold">Modèle:</span> {diagnostic.model}
                      </div>
                    )}
                  </div>

                  {diagnostic.address && (
                    <div className="pt-2 border-t">
                      <p className="font-semibold text-sm mb-1">Adresse:</p>
                      <p className="text-gray-700">{diagnostic.address}</p>
                    </div>
                  )}

                  <div className="pt-2 border-t">
                    <p className="font-semibold text-sm mb-1">Problème:</p>
                    <p className="text-gray-700">{diagnostic.problem}</p>
                  </div>
                </div>
                
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={() => openDetailModal(diagnostic, 'diagnostic')}
                    className="inline-flex items-center px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors space-x-2"
                  >
                    <Eye size={16} />
                    <span>Consulter</span>
                  </button>
                  <button
                    onClick={() => handleOpenMessageModal(diagnostic, 'diagnostic')}
                    className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors space-x-2"
                  >
                    <MessageSquare size={16} />
                    <span>Répondre par email</span>
                  </button>
                  <a
                    href={`tel:${diagnostic.phone}`}
                    className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors space-x-2"
                  >
                    <Phone size={16} />
                    <span>Appeler</span>
                  </a>
                  {!showArchived && (
                    <button
                      onClick={() => archiveItem(diagnostic.id, 'diagnostic')}
                      className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors space-x-2"
                    >
                      <Archive size={16} />
                      <span>Archiver</span>
                    </button>
                  )}
                  <button
                    onClick={() => deleteItem(diagnostic.id, 'diagnostic')}
                    className="inline-flex items-center px-4 py-2 bg-red-800 hover:bg-red-900 text-white rounded-lg font-medium transition-colors space-x-2"
                    title="Supprimer définitivement"
                  >
                    <Trash2 size={16} />
                    <span>Supprimer</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    );
  };

  const renderSMS = () => {
    const newMessagesCount = smsMessages.filter(m => m.isNew).length;

    const handleReply = async (sms) => {
      if (!smsReplyText.trim()) return;
      setSmsSendingReply(true);
      try {
        const token = localStorage.getItem('admin_token');
        const resp = await fetch(`${API_URL}/api/sms-messages/${sms.id}/reply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ reply: smsReplyText })
        });
        if (resp.ok) {
          setSmsReplyText('');
          setSmsReplyingTo(null);
          fetchSmsMessages();
          alert(`✅ SMS envoyé à ${sms.from_number}`);
        } else {
          const d = await resp.json();
          alert(`❌ ${d.detail || 'Erreur envoi'}`);
        }
      } catch (e) { alert('❌ Erreur réseau'); }
      finally { setSmsSendingReply(false); }
    };

    const handleDeleteSms = async (smsId) => {
      if (!window.confirm('Supprimer ce SMS ?')) return;
      const token = localStorage.getItem('admin_token');
      try {
        await fetch(`${API_URL}/api/sms-messages/${smsId}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        });
        fetchSmsMessages();
      } catch (e) { alert('❌ Erreur réseau'); }
    };

    const handleDeleteAll = async () => {
      if (!window.confirm('⚠️ Supprimer TOUS les SMS ?')) return;
      if (!window.confirm('Dernière confirmation ?')) return;
      const token = localStorage.getItem('admin_token');
      try {
        const resp = await fetch(`${API_URL}/api/sms-messages/bulk/all`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) { fetchSmsMessages(); alert('✅ Tous les SMS supprimés'); }
      } catch (e) { alert('❌ Erreur réseau'); }
    };

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-white">📱 SMS Entrants</h2>
            {newMessagesCount > 0 && (
              <span className="bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full animate-pulse">
                {newMessagesCount} nouveau{newMessagesCount > 1 ? 'x' : ''}
              </span>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-gray-400 text-sm">Numéro : <strong className="text-white">+1 (350) 900-6077</strong></span>
            <Button onClick={handleDeleteAll} className="bg-red-800 hover:bg-red-900 text-white flex items-center gap-1 text-sm">
              <Trash2 size={14}/> Tout supprimer
            </Button>
          </div>
        </div>

        {/* Liste des SMS */}
        {smsMessages.length === 0 ? (
          <Card className="border-gray-600 bg-gray-800">
            <CardContent className="py-16 text-center">
              <div className="text-6xl mb-4">📱</div>
              <h3 className="text-xl font-bold text-white mb-2">Aucun SMS reçu</h3>
              <p className="text-gray-400">Les SMS envoyés au <strong className="text-white">+1 (350) 900-6077</strong> apparaîtront ici</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {smsMessages.map((sms) => (
              <Card key={sms.id} className={`border-l-4 transition-all ${sms.isNew ? 'border-l-red-500 bg-gray-750 shadow-lg shadow-red-500/10' : 'border-l-gray-600 bg-gray-800'}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    {/* Infos client */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-bold text-white text-base">{sms.from_number}</span>
                        {sms.isNew && <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full animate-pulse font-bold">NOUVEAU</span>}
                        {sms.replied && <span className="bg-green-700 text-white text-xs px-2 py-0.5 rounded-full">✅ Répondu</span>}
                        <span className="text-gray-400 text-xs">{sms.timestamp ? new Date(sms.timestamp).toLocaleString('fr-FR') : ''}</span>
                      </div>
                      <p className="text-gray-200 text-sm bg-gray-700 rounded-lg p-3 mt-1">{sms.message_body}</p>
                      {sms.replied && sms.reply_body && (
                        <p className="text-green-300 text-xs bg-green-900/30 rounded p-2 mt-1">
                          ↩ Votre réponse : {sms.reply_body}
                        </p>
                      )}
                      {/* Zone de réponse */}
                      {smsReplyingTo === sms.id && (
                        <div className="mt-3 flex gap-2">
                          <input
                            type="text"
                            value={smsReplyText}
                            onChange={e => setSmsReplyText(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply(sms)}
                            placeholder="Votre réponse SMS..."
                            className="flex-1 bg-gray-700 border border-gray-500 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                            autoFocus
                          />
                          <Button onClick={() => handleReply(sms)} disabled={smsSendingReply || !smsReplyText.trim()}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4">
                            {smsSendingReply ? '...' : 'Envoyer'}
                          </Button>
                          <Button onClick={() => { setSmsReplyingTo(null); setSmsReplyText(''); }}
                            className="bg-gray-600 hover:bg-gray-700 text-white text-sm px-3">✕</Button>
                        </div>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <button onClick={() => { setSmsReplyingTo(sms.id); setSmsReplyText(''); }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap">
                        📨 Répondre
                      </button>
                      <button onClick={() => handleDeleteSms(sms.id)}
                        className="bg-red-800 hover:bg-red-900 text-white px-3 py-1.5 rounded text-xs font-medium">
                        🗑️ Supprimer
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">Chargement...</div>
        </div>
      </div>
    );
  }

  const tabConfig = {
    rdv: {
      label: 'Rendez-vous',
      color: 'blue',
      icon: Calendar,
      count: showArchived ? archivedAppointments.length : recentAppointments.length
    },
    devis: {
      label: 'Devis',
      color: 'green',
      icon: FileText,
      count: showArchived ? archivedQuotes.length : recentQuotes.length
    },
    diagnostic: {
      label: 'Diagnostic',
      color: 'orange',
      icon: AlertCircle,
      count: showArchived ? archivedDiagnostics.length : recentDiagnostics.length
    },
    studio: {
      label: 'Studio IA',
      color: 'purple',
      icon: Camera,
      count: 0
    },
    pricing: {
      label: 'Tarification',
      color: 'yellow',
      icon: Calculator,
      count: 0
    },
    bot: {
      label: 'Bot IA',
      color: 'blue',
      icon: Bot,
      count: botConversations.filter(c => c.isNew && !c.archived).length
    }
  };

  // Fonction de reset d'urgence complète
  const forceResetAll = async () => {
    try {
      console.log('🚨 RESET D\'URGENCE LANCÉ');
      
      // 1. Arrêter le son IMMÉDIATEMENT
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = null;
        console.log('🔇 Intervalle sonore arrêté');
      }
      
      // 2. Réinitialiser TOUS les états
      setHasNewNotification(false);
      setLastNotificationCount(0);
      console.log('✅ États locaux réinitialisés');
      
      // 3. Appeler l'endpoint de reset d'urgence
      const token = localStorage.getItem('admin_token');
      console.log('📡 Appel de l\'endpoint de reset...');
      
      const response = await fetch(`${API_URL}/api/debug/force-reset-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Réponse du serveur:', data);
        
        // 4. Forcer le rechargement complet (vide le cache)
        alert(`✅ RESET COMPLET RÉUSSI !\n\n${data.before.total} notifications ont été supprimées.\n\nLa page va se recharger pour appliquer les changements.`);
        
        // Vider le localStorage des notifications
        localStorage.removeItem('lastNotificationCount');
        
        // Recharger en forçant le cache à se vider
        window.location.href = window.location.href;
      } else {
        const errorText = await response.text();
        console.error('❌ Erreur serveur:', errorText);
        alert('❌ Erreur lors du reset: ' + errorText);
      }
    } catch (error) {
      console.error('❌ Erreur reset:', error);
      alert('❌ Erreur réseau: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <audio ref={rdvAlertAudioRef} src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 shadow-2xl relative">
        {/* Alerte clignotante pour nouvelles demandes */}
        {hasNewRequests && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 animate-pulse"></div>
        )}
        
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
                <Calendar className="w-10 h-10" />
                <span>GESTION GLOBALE - PHONELAB 69</span>
                {hasNewRequests && (
                  <span className="ml-4 px-4 py-2 bg-red-500 text-white text-lg font-bold rounded-full animate-pulse flex items-center space-x-2">
                    <AlertCircle className="w-6 h-6 animate-bounce" />
                    <span>{totalNewRequests} NOUVELLE{totalNewRequests > 1 ? 'S' : ''} DEMANDE{totalNewRequests > 1 ? 'S' : ''}</span>
                  </span>
                )}
              </h1>
              <p className="text-blue-100 mt-1">Tableau de bord administrateur</p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Système d'alertes désactivé */}
              
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllSeen}
                  className="relative p-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-all shadow-lg"
                >
                  <CheckCheck className="w-6 h-6" />
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {unreadCount}
                  </span>
                </button>
              )}
              
              <Button
                onClick={handleOpenNewMessageModal}
                className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700"
              >
                <Plus size={18} />
                <Mail size={18} />
              </Button>
              
              <Button
                onClick={handleLogout}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700"
              >
                <LogOut className="w-5 h-5" />
                <span>Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Barre de recherche (masquée pour l'onglet Bot) */}
        {activeTab !== 'bot' && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom, email, téléphone, marque, modèle..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-lg"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Onglets */}
        <div className="bg-white rounded-2xl shadow-xl p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            {Object.entries(tabConfig).map(([key, config]) => {
              const Icon = config.icon;
              const isActive = activeTab === key;
              const colorClasses = {
                blue: isActive ? 'bg-blue-500 text-white' : 'text-blue-600 hover:bg-blue-50',
                green: isActive ? 'bg-green-500 text-white' : 'text-green-600 hover:bg-green-50',
                orange: isActive ? 'bg-orange-500 text-white' : 'text-orange-600 hover:bg-orange-50',
                purple: isActive ? 'bg-purple-600 text-white' : 'text-purple-400 hover:bg-purple-900/30',
                yellow: isActive ? 'bg-yellow-500 text-black' : 'text-yellow-400 hover:bg-yellow-900/30',
              };
              
              // Compter les nouvelles demandes par catégorie
              let newItemsCount = 0;
              if (key === 'rdv') {
                newItemsCount = appointments.filter(a => a.isNew).length;
              } else if (key === 'devis') {
                newItemsCount = quotes.filter(q => q.isNew).length;
              } else if (key === 'diagnostic') {
                newItemsCount = diagnostics.filter(d => d.isNew).length;
              } else if (key === 'bot') {
                newItemsCount = botConversations.filter(c => c.isNew).length;
              }
              
              return (
                <button
                  key={key}
                  onClick={() => handleTabClick(key)}
                  className={`relative flex-1 min-w-[200px] flex items-center justify-center space-x-3 px-6 py-4 rounded-xl font-semibold text-lg transition-all ${colorClasses[config.color]}`}
                >
                  <Icon size={24} />
                  <span>{config.label}</span>
                  {/* Badge avec compteur */}
                  {config.count > 0 && (
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                      isActive ? 'bg-white/30' : 'bg-white/20'
                    }`}>
                      {config.count}
                    </span>
                  )}
                  {/* Badge clignotant si nouvelles demandes */}
                  {config.count > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center animate-pulse">
                      {config.count > 9 ? '9+' : config.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bouton Voir les archives / Récents (masqué pour l'onglet Bot) */}
        {activeTab !== 'bot' && activeTab !== 'whatsapp' && activeTab !== 'studio' && activeTab !== 'pricing' && (
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">
              {showArchived ? '📦 Archives' : '📋 Demandes Récentes'}
            </h2>
            <div className="flex items-center gap-3">
              {/* Bouton Supprimer sélection RDV uniquement */}
              {activeTab === 'rdv' && selectedRdvIds.size > 0 && (
                <Button
                  onClick={deleteSelectedRdv}
                  className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white animate-pulse"
                >
                  <Trash2 size={16} />
                  <span>Supprimer la sélection ({selectedRdvIds.size})</span>
                </Button>
              )}
              <Button
                onClick={() => deleteAllItems(activeTab)}
                className="flex items-center space-x-2 bg-red-800 hover:bg-red-900 text-white"
                title="Supprimer définitivement tous les éléments de cet onglet"
              >
                <Trash2 size={16} />
                <span>Tout supprimer</span>
              </Button>
              <Button
                onClick={() => setShowArchived(!showArchived)}
                className={`flex items-center space-x-2 ${
                  showArchived
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {showArchived ? <Eye size={18} /> : <Archive size={18} />}
                <span>{showArchived ? 'Voir les récents' : 'Voir les archives'}</span>
              </Button>
            </div>
          </div>
        )}
        {activeTab === 'whatsapp' && (
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">📋 Demandes Récentes</h2>
          </div>
        )}

        {/* Contenu de l'onglet actif */}
        {activeTab === 'rdv' && renderAppointments()}
        {activeTab === 'devis' && renderQuotes()}
        {activeTab === 'diagnostic' && renderDiagnostics()}
        {activeTab === 'studio' && <StudioPhotos />}
        {activeTab === 'pricing' && <PricingTool />}
        {activeTab === 'bot' && <BotManagementComplete />}
      </div>
      
      {/* Modals */}
      {showMessageModal && selectedQuote && (
        <SendMessageModal
          quote={selectedQuote}
          onClose={handleCloseMessageModal}
          onMessageSent={handleMessageSent}
        />
      )}
      
      {showNewMessageModal && (
        <NewMessageModal
          onClose={handleCloseNewMessageModal}
          onMessageSent={handleNewMessageSent}
        />
      )}
      
      {/* Modal Modifier RDV */}
      {showModifyModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">✏️ Modifier le RDV</h3>
              <button
                onClick={closeModifyModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Client:</strong> {selectedAppointment.name}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>RDV actuel:</strong> {new Date(selectedAppointment.date).toLocaleDateString('fr-FR')} à {selectedAppointment.time}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Nouvelle date</label>
                <input
                  type="date"
                  id="new-date"
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  defaultValue={selectedAppointment.date}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Nouvelle heure</label>
                <input
                  type="time"
                  id="new-time"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  defaultValue={selectedAppointment.time}
                />
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <p className="text-sm text-yellow-800">
                  ⚠️ Un email sera automatiquement envoyé au client pour l'informer du changement.
                </p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    const newDate = document.getElementById('new-date').value;
                    const newTime = document.getElementById('new-time').value;
                    if (newDate && newTime) {
                      modifyAppointment(newDate, newTime);
                    } else {
                      alert('Veuillez remplir tous les champs');
                    }
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition-colors"
                >
                  ✅ Confirmer la modification
                </button>
                <button
                  onClick={closeModifyModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 rounded-lg transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de consultation détaillée */}
      {showDetailModal && (
        <DetailModal 
          item={selectedDetail}
          type={detailType}
          onClose={closeDetailModal}
        />
      )}

      {/* Modal universelle d'envoi de message */}
      {showUniversalMessageModal && selectedItemForMessage && (
        <UniversalMessageModal
          item={selectedItemForMessage}
          type={messageItemType}
          onClose={handleCloseUniversalModal}
          onMessageSent={handleMessageSent}
        />
      )}
    </div>
  );
};
